import React from 'react';
import classNames from 'classnames';
import { BaseItem, Column } from '@lib/components/Table/types';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import {
  ORDERING_DIRECTION_ASC,
  ORDERING_DIRECTION_DESC,
} from '@lib/enums/urls';
import styles from './TableHead.module.scss';

type Props<T extends BaseItem> = {
  column: Column<T>;
  index: number;
  onOrderingChange: (column: Column<T>) => void;
  orderingField?: string;
  orderingDirection?: string;
};

function TableHeadCell<T extends BaseItem>(props: Props<T>) {
  const { column, index, onOrderingChange, orderingField, orderingDirection } =
    props;

  const {
    Header,
    bordered,
    headerTooltipInfo,
    hideHeader,
    id,
    isCustomHeader,
    isSingleLineHeader,
    maxWidth,
    minWidth,
    sortable,
    width,
  } = column;
  const cellStyle = {
    width,
    minWidth,
    maxWidth,
  };
  const cellClassName = classNames(column.cellClassName, {
    [styles.sortingCell]: sortable,
    [styles.borderedCell]: bordered,
    [styles.singleLineCell]: isSingleLineHeader,
  });
  const cellContentClassName = classNames({
    [styles.flexCellContent]: sortable || headerTooltipInfo,
  });
  const onCellClick = sortable ? () => onOrderingChange(column) : undefined;

  return (
    <th
      id={`${id}--${index}`}
      className={cellClassName}
      data-test-id={id ? `table__header-cell-${id}` : undefined}
      style={cellStyle}
      onClick={onCellClick}
    >
      {isCustomHeader ? (
        Header
      ) : (
        <div className={cellContentClassName}>
          {hideHeader ? null : (
            <>
              <div className={styles.headerCellContent}>{Header}</div>
              {headerTooltipInfo && (
                <Tooltip
                  body={headerTooltipInfo.tooltipBody}
                  contentClassName={styles.cellTooltipIcon}
                >
                  <MaterialIcon
                    cursor="help"
                    icon={headerTooltipInfo.icon}
                    size="md-16"
                    symbolsOutlined={headerTooltipInfo.symbolsOutlined}
                  />
                </Tooltip>
              )}
              {sortable && (
                <MaterialIcon
                  icon="arrow_downward"
                  size="md-16"
                  className={classNames(styles.sortingIcon, {
                    [styles.sortingIconAsc]:
                      orderingField === id &&
                      orderingDirection === ORDERING_DIRECTION_ASC,
                    [styles.sortingIconDesc]:
                      orderingField === id &&
                      orderingDirection === ORDERING_DIRECTION_DESC,
                  })}
                />
              )}
            </>
          )}
        </div>
      )}
    </th>
  );
}

export default TableHeadCell;
